import * as THREE from 'three';
import { createNoise2D } from 'simplex-noise';
import { CAMERA, MAVERICK } from '../core/SimConfig.js';
import { WeaponMode } from '../weapons/WeaponSystem.js';
import { clamp, smoothstep } from '../utils.js';

const _fpvWorld = new THREE.Vector3();
const _lookQuat = new THREE.Quaternion();
const _euler = new THREE.Euler(0, 0, 0, 'YXZ');

// Airframe-locked look (manual schemes)
const _bodyEuler = new THREE.Euler(0, 0, 0, 'YXZ');
const _bodyQuat = new THREE.Quaternion();

// Shake + head-inertia rotation, layered on after the Maverick aim blend
const _dynEuler = new THREE.Euler(0, 0, 0, 'YXZ');
const _dynQuat = new THREE.Quaternion();

// Maverick aim-pose scratch
const _aimEye = new THREE.Vector3();
const _tvCenter = new THREE.Vector3();
const _aimMatrix = new THREE.Matrix4();
const _aimQuat = new THREE.Quaternion();

// Scratch vectors for spring-damper computation
const _accelWorld = new THREE.Vector3();
const _accelLocal = new THREE.Vector3();
const _invQuat = new THREE.Quaternion();
const _springForce = new THREE.Vector3();
const _dampForce = new THREE.Vector3();
const _angAccelLocal = new THREE.Vector3();
const _springTorque = new THREE.Vector3();
const _dampTorque = new THREE.Vector3();
const _localOffset = new THREE.Vector3();
const _omegaLocal = new THREE.Vector3();

export default class CockpitCamera {
  constructor({ aircraft, controller }) {
    this.aircraft = aircraft;
    this.controller = controller;
    this.weaponSystem = null;       // set after construction
    this.maverickSeeker = null;     // set after construction — drives the aim pose

    // Output — CameraManager reads these
    this.position = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();

    // Find the FPV empty node baked into the model
    this.fpvNode = aircraft.group.getObjectByName('FPV');
    if (!this.fpvNode) {
      console.warn('CockpitCamera: "FPV" node not found in model, falling back to offset');
    }

    // Maverick aim pose: head shifts to FPV_Maverick and locks onto the `_TV`
    // plane while the player operates the Maverick. Blended in/out smoothly.
    this.fpvMaverickNode = aircraft.group.getObjectByName(MAVERICK.nodeFPV) || null;
    this.tvNode = aircraft.group.getObjectByName(MAVERICK.nodeTV) || null;
    this._aimBlend = 0;

    // Smoothing
    this.lookSmoothing = CAMERA.cockpit.lookSmoothing;
    this.rollSmoothing = CAMERA.cockpit.rollSmoothing;
    this.rollFraction = CAMERA.cockpit.rollFraction;
    this._smoothYaw = controller.lookYaw;
    this._smoothPitch = controller.lookPitch;
    this._smoothRoll = 0;
    // Body-relative head look (every scheme but Basic) and the 0..1 blend
    // between the two frames: 0 = horizon-stabilised look-to-steer view,
    // 1 = locked to the airframe. Eased so a mid-flight scheme change glides.
    this._smoothHeadYaw = 0;
    this._smoothHeadPitch = 0;
    this._frameBlend = controller.bodyLook ? 1 : 0;

    // Head spring-damper state (local-space displacement and velocity)
    this._headOffset = new THREE.Vector3();     // displacement from rest (local space)
    this.localOffset = new THREE.Vector3();     // combined offset (inertia + lean) in local space
    this._headVelocity = new THREE.Vector3();   // velocity of displacement (local space)
    this._headRotOffset = new THREE.Vector3();  // rotational displacement (pitch, yaw, roll)
    this._headRotVelocity = new THREE.Vector3();// rotational velocity
    this._prevVelocity = new THREE.Vector3();   // previous frame velocity for accel calc
    this._prevAngVelocity = new THREE.Vector3();// previous frame angular velocity
    // Sense-of-flight head tilt (manual schemes): roll relative to the airframe
    this._sofRoll = 0;
    this._sofRollVel = 0;

    // Runway shake — simplex noise for rotation and position axes
    this._shakePitch = createNoise2D();
    this._shakeYaw = createNoise2D();
    this._shakeRoll = createNoise2D();
    this._shakePosX = createNoise2D();
    this._shakePosY = createNoise2D();
    this._shakePosZ = createNoise2D();
    this._shakeTime = 0;

    this._zoomBlend = 0;          // 0 = normal, 1 = fully zoomed

    this._nvgTrauma = 0;          // 0..1 — goggle flip jolt, set by kickNVGShake()

    this._initialized = false;
  }

  // Kick the goggle-flip head shake. Called by App when NVGs are toggled on/off.
  kickNVGShake() {
    this._nvgTrauma = 1;
  }

  /** Jump straight to the controller's look frame (no glide) — at launch. */
  snapFrame() {
    this._frameBlend = this.controller.bodyLook ? 1 : 0;
  }

  update(dt) {
    const ctrl = this.controller;
    const aircraft = this.aircraft;
    const spring = CAMERA.cockpit.headSpring;

    // ── Compute aircraft acceleration in local space ─────────────────────
    _accelWorld.copy(aircraft.velocity).sub(this._prevVelocity).divideScalar(Math.max(dt, 0.001));
    this._prevVelocity.copy(aircraft.velocity);

    // No gravity bias — the head reacts to acceleration changes, not sustained g

    // Transform world acceleration into aircraft-local frame
    _invQuat.copy(aircraft.quaternion).invert();
    _accelLocal.copy(_accelWorld).applyQuaternion(_invQuat);

    // ── Positional spring-damper ─────────────────────────────────────────
    // Head resists aircraft acceleration (inertia), spring pulls back to rest
    _springForce.copy(this._headOffset).multiplyScalar(-spring.stiffness);
    _dampForce.copy(this._headVelocity).multiplyScalar(-spring.damping);

    // Inertial "force" — opposite to felt acceleration, scaled for reasonable displacement
    this._headVelocity.x += (_springForce.x + _dampForce.x - _accelLocal.x * 0.01) * dt;
    this._headVelocity.y += (_springForce.y + _dampForce.y - _accelLocal.y * 0.01) * dt;
    this._headVelocity.z += (_springForce.z + _dampForce.z - _accelLocal.z * 0.01) * dt;

    this._headOffset.x += this._headVelocity.x * dt;
    this._headOffset.y += this._headVelocity.y * dt;
    this._headOffset.z += this._headVelocity.z * dt;

    // Clamp displacement to max radius
    const len = this._headOffset.length();
    if (len > spring.maxDisplacement) {
      this._headOffset.multiplyScalar(spring.maxDisplacement / len);
    }

    // ── Rotational spring-damper ─────────────────────────────────────────
    // Angular acceleration from change in aircraft angular velocity
    _angAccelLocal.copy(aircraft.angularVelocity).sub(this._prevAngVelocity).divideScalar(Math.max(dt, 0.001));
    this._prevAngVelocity.copy(aircraft.angularVelocity);

    _springTorque.copy(this._headRotOffset).multiplyScalar(-spring.rotStiffness);
    _dampTorque.copy(this._headRotVelocity).multiplyScalar(-spring.rotDamping);

    // Head rotation lags behind angular acceleration changes
    this._headRotVelocity.x += (_springTorque.x + _dampTorque.x - _angAccelLocal.x * 0.15) * dt;
    this._headRotVelocity.y += (_springTorque.y + _dampTorque.y - _angAccelLocal.y * 0.15) * dt;
    // Roll (z) uses its own tighter spring so bank-sway settles fast and small
    this._headRotVelocity.z += (
      -this._headRotOffset.z * spring.rollStiffness
      - this._headRotVelocity.z * spring.rollDamping
      - _angAccelLocal.z * 0.15
    ) * dt;

    this._headRotOffset.x += this._headRotVelocity.x * dt;
    this._headRotOffset.y += this._headRotVelocity.y * dt;
    this._headRotOffset.z += this._headRotVelocity.z * dt;

    // Clamp rotational displacement
    const rotLen = this._headRotOffset.length();
    if (rotLen > spring.maxRotation) {
      this._headRotOffset.multiplyScalar(spring.maxRotation / rotLen);
    }

    // ── FPV node world position ──────────────────────────────────────────
    if (this.fpvNode) {
      this.fpvNode.getWorldPosition(_fpvWorld);
    } else {
      const [, upOff, fwdOff] = CAMERA.cockpit.fallbackOffset;
      _fpvWorld.copy(aircraft.position)
        .add(aircraft.upVector.clone().multiplyScalar(upOff))
        .add(aircraft.forwardVector.clone().multiplyScalar(fwdOff));
    }

    // ── Zoom blend (smooth transition for pitch offset) ───────────────────
    const zoomTarget = ctrl.zooming ? 1 : 0;
    const zoomAlpha = 1 - Math.exp(-CAMERA.fovSmoothing * dt);
    this._zoomBlend += (zoomTarget - this._zoomBlend) * zoomAlpha;

    // ── Smooth look (relative to aircraft orientation) ───────────────────
    const lookAlpha = 1 - Math.exp(-this.lookSmoothing * dt);
    this._smoothYaw += (ctrl.lookYaw - this._smoothYaw) * lookAlpha;
    this._smoothPitch += (ctrl.lookPitch - this._smoothPitch) * lookAlpha;
    this._smoothHeadYaw += (ctrl.headYaw - this._smoothHeadYaw) * lookAlpha;
    this._smoothHeadPitch += (ctrl.headPitch - this._smoothHeadPitch) * lookAlpha;
    const frameAlpha = 1 - Math.exp(-CAMERA.cockpit.frameBlendRate * dt);
    this._frameBlend += ((ctrl.bodyLook ? 1 : 0) - this._frameBlend) * frameAlpha;
    const fb = this._frameBlend;

    // ── Smooth roll (fraction of aircraft bank) ────────────────────────
    const right = aircraft.rightVector;
    const up = aircraft.upVector;
    const aircraftRoll = Math.atan2(right.y, up.y);
    const targetRoll = -Math.max(-Math.PI / 2, Math.min(Math.PI / 2, aircraftRoll * this.rollFraction));
    const rollAlpha = 1 - Math.exp(-this.rollSmoothing * dt);
    this._smoothRoll += (targetRoll - this._smoothRoll) * rollAlpha;

    // ── Sense of flight (manual schemes) ─────────────────────────────────
    // Head tilt relative to the airframe (+ = top of the head to the left):
    // lags a roll, levels back toward the horizon in a held bank, and tips
    // away from side force. Faded in with the body-locked frame, so Basic's
    // horizon-stabilised view never gets it.
    const sof = CAMERA.cockpit.senseOfFlight;
    _omegaLocal.copy(aircraft.angularVelocity).applyQuaternion(_invQuat);
    const rollRateR = -_omegaLocal.z; // + = rolling right
    const vertical = Math.abs(aircraft.forwardVector.y);
    const levelFade = 1 - smoothstep((vertical - sof.verticalFade[0]) / (sof.verticalFade[1] - sof.verticalFade[0]));
    const sofTarget = fb * (
      clamp(rollRateR * sof.rollLag, -sof.rollLagMax, sof.rollLagMax)
      + Math.sin(-aircraftRoll) * sof.horizonLevel * levelFade
      + clamp((aircraft.gForceLocal?.x || 0) * sof.lateralG, -sof.lateralGMax, sof.lateralGMax)
    );
    const sdt = Math.min(dt, 0.05);
    this._sofRollVel += (sof.stiffness * (sofTarget - this._sofRoll) - sof.damping * this._sofRollVel) * sdt;
    this._sofRoll += this._sofRollVel * sdt;

    // ── Shake (runway + gun) ───────────────────────────────────────────
    const rwShake = CAMERA.cockpit.runwayShake;
    const gunShake = CAMERA.cockpit.gunShake;
    let shakePitch = 0, shakeYaw = 0, shakeRoll = 0;
    let shakeX = 0, shakeY = 0, shakeZ = 0;

    // Runway shake — ground only, scales with speed
    if (aircraft.onGround) {
      const speed = aircraft.airspeed;
      const t = Math.max(0, Math.min(1,
        (speed - rwShake.speedMin) / (rwShake.speedMax - rwShake.speedMin)));
      if (t > 0) {
        this._shakeTime += dt * rwShake.frequency;
        const amp = rwShake.amplitude * t;
        shakePitch = this._shakePitch(this._shakeTime, 0) * amp;
        shakeYaw   = this._shakeYaw(this._shakeTime, 1) * amp;
        shakeRoll  = this._shakeRoll(this._shakeTime, 2) * amp * 0.5;

        const posAmp = rwShake.posAmplitude * t;
        shakeX = this._shakePosX(this._shakeTime, 3) * posAmp;
        shakeY = this._shakePosY(this._shakeTime, 4) * posAmp;
        shakeZ = this._shakePosZ(this._shakeTime, 5) * posAmp;
      }
    }

    // Gun shake — only when GAU-8 is the active weapon and rounds are spawning
    if (this.weaponSystem && this.weaponSystem.firing && this.weaponSystem.mode === WeaponMode.GAU8 && this.weaponSystem.ammo > 0) {
      this._shakeTime += dt * gunShake.frequency;
      const gAmp = gunShake.amplitude;
      shakePitch += this._shakePitch(this._shakeTime, 10) * gAmp;
      shakeYaw   += this._shakeYaw(this._shakeTime, 11) * gAmp;
      shakeRoll  += this._shakeRoll(this._shakeTime, 12) * gAmp * 0.5;

      const gPosAmp = gunShake.posAmplitude;
      shakeX += this._shakePosX(this._shakeTime, 13) * gPosAmp;
      shakeY += this._shakePosY(this._shakeTime, 14) * gPosAmp;
      shakeZ += this._shakePosZ(this._shakeTime, 15) * gPosAmp;
    }

    // Buffet shake — high-AoA airframe buffeting, ramps in smoothly with AoA
    // (Aircraft.buffetIntensity, shared with the cockpit buffet rumble).
    // In-flight only (runway shake covers the ground).
    const buffet = CAMERA.cockpit.buffetShake;
    let buffetT = aircraft.buffetIntensity;
    // Engine-damage tremor — a subtle continuous rough-running shake held for as
    // long as any engine is knocked out, regardless of AoA/speed. Reuses the
    // buffet noise path; the stronger of the two intensities wins.
    if (aircraft.engineLeftOut || aircraft.engineRightOut) {
      buffetT = Math.max(buffetT, buffet.damageIntensity);
    }
    if (buffetT > 0) {
      this._shakeTime += dt * buffet.frequency;
      // Positional only — buffet jostles the head, but leaves the view angle
      // steady so it doesn't fight the pilot's aim.
      const bPosAmp = buffet.posAmplitude * buffetT;
      shakeX += this._shakePosX(this._shakeTime, 23) * bPosAmp;
      shakeY += this._shakePosY(this._shakeTime, 24) * bPosAmp;
      shakeZ += this._shakePosZ(this._shakeTime, 25) * bPosAmp;
    }

    // Missile-impact jolt — a quick, significant shake on an enemy missile
    // strike. Scales with aircraft.impactTrauma² for a punchy hit that fades
    // fast (trauma is set to 1 on impact and decayed in Aircraft.update).
    // Shakes both view angle and head position for a jarring effect.
    const trauma = aircraft.impactTrauma || 0;
    if (trauma > 0) {
      const impact = CAMERA.cockpit.impactShake;
      const k = trauma * trauma;
      this._shakeTime += dt * impact.frequency;
      const iAmp = impact.amplitude * k;
      shakePitch += this._shakePitch(this._shakeTime, 30) * iAmp;
      shakeYaw   += this._shakeYaw(this._shakeTime, 31) * iAmp;
      shakeRoll  += this._shakeRoll(this._shakeTime, 32) * iAmp;
      const iPos = impact.posAmplitude * k;
      shakeX += this._shakePosX(this._shakeTime, 33) * iPos;
      shakeY += this._shakePosY(this._shakeTime, 34) * iPos;
      shakeZ += this._shakePosZ(this._shakeTime, 35) * iPos;
    }

    // Missile-launch turbulence — a brief, subtle wobble as a rocket leaves the
    // rail (AIM-9 / Maverick). Scales with aircraft.launchTrauma² so it kicks
    // then settles fast (trauma set to 1 on launch, decayed in Aircraft.update).
    const launchTrauma = aircraft.launchTrauma || 0;
    if (launchTrauma > 0) {
      const launch = CAMERA.cockpit.launchShake;
      const k = launchTrauma * launchTrauma;
      this._shakeTime += dt * launch.frequency;
      const lAmp = launch.amplitude * k;
      shakePitch += this._shakePitch(this._shakeTime, 40) * lAmp;
      shakeYaw   += this._shakeYaw(this._shakeTime, 41) * lAmp;
      shakeRoll  += this._shakeRoll(this._shakeTime, 42) * lAmp;
      const lPos = launch.posAmplitude * k;
      shakeX += this._shakePosX(this._shakeTime, 43) * lPos;
      shakeY += this._shakePosY(this._shakeTime, 44) * lPos;
      shakeZ += this._shakePosZ(this._shakeTime, 45) * lPos;
    }

    // NVG flip jolt — a quick head bobble when the goggles snap down/up. Trauma
    // is set to 1 by kickNVGShake() and decays here; the shake scales with
    // trauma² so it kicks then settles fast. Shakes view angle + head position.
    if (this._nvgTrauma > 0) {
      const nvg = CAMERA.cockpit.nvgShake;
      this._nvgTrauma = Math.max(0, this._nvgTrauma - nvg.decay * dt);
      const k = this._nvgTrauma * this._nvgTrauma;
      this._shakeTime += dt * nvg.frequency;
      const nAmp = nvg.amplitude * k;
      shakePitch += this._shakePitch(this._shakeTime, 50) * nAmp;
      shakeYaw   += this._shakeYaw(this._shakeTime, 51) * nAmp;
      shakeRoll  += this._shakeRoll(this._shakeTime, 52) * nAmp;
      const nPos = nvg.posAmplitude * k;
      shakeX += this._shakePosX(this._shakeTime, 53) * nPos;
      shakeY += this._shakePosY(this._shakeTime, 54) * nPos;
      shakeZ += this._shakePosZ(this._shakeTime, 55) * nPos;
    }

    // Fade head inertia/lean to zero when zoomed (physics keeps running, just not visible)
    const headEffect = 1 - this._zoomBlend;

    // ── Base look orientation ──────────────────────────────────────────────
    // Basic (look-to-steer): smoothed world look + a fraction of the bank, so
    // the view stays near level with the horizon it's steering by. Manual
    // schemes: the head's look rides the airframe, full roll and all — loops
    // and rolls carry the view with them, and the inertia springs below are
    // what keep it feeling like a head rather than a gun camera.
    // Shake and head-inertia rotation are deliberately excluded here — they're
    // layered on AFTER the Maverick aim blend (below) so they survive aim mode:
    // the head still rattles and leans while the view is locked to the TV.
    const pitchOffset = CAMERA.cockpit.pitchOffset * Math.max(1 - this._zoomBlend, 0.2);
    _euler.set(this._smoothPitch + pitchOffset, this._smoothYaw, -this._smoothRoll);
    _lookQuat.setFromEuler(_euler);
    this.quaternion.copy(_lookQuat);
    if (fb > 1e-3) {
      _bodyEuler.set(this._smoothHeadPitch + pitchOffset, this._smoothHeadYaw, 0);
      _bodyQuat.copy(aircraft.quaternion).multiply(_dynQuat.setFromEuler(_bodyEuler));
      if (fb > 0.999) this.quaternion.copy(_bodyQuat);
      else this.quaternion.slerp(_bodyQuat, fb);
    }

    // ── Base position: FPV node (head inertia/lean folded in after aim blend) ──
    // Lean: pilot slides head laterally when looking to the side
    // lookYaw is world-space, so subtract aircraft heading to get head-relative yaw
    const fwd = aircraft.forwardVector;
    const aircraftYaw = Math.atan2(-fwd.x, -fwd.z);
    // Body-locked, the head yaw already *is* relative, and the lean axis rolls
    // with the jet.
    const relativeYaw = (this._smoothYaw - aircraftYaw) * (1 - fb) + this._smoothHeadYaw * fb;
    const rollFactor = Math.cos(aircraftRoll) * (1 - fb) + fb;
    const lean = Math.sin(relativeYaw) * CAMERA.cockpit.headLean * rollFactor * headEffect;
    _localOffset.copy(this._headOffset).multiplyScalar(headEffect);
    _localOffset.x += lean;
    this.localOffset.copy(_localOffset);
    _localOffset.applyQuaternion(aircraft.quaternion);

    this.position.copy(_fpvWorld);

    // ── Maverick aim pose ────────────────────────────────────────────────
    // Ease the head from the normal FPV pose to FPV_Maverick, looking straight
    // at the `_TV` plane while the player operates the Maverick. Only the LOOK
    // pose (base position + orientation) is blended — shake and head dynamics
    // are added on afterwards, so the TV-locked view still feels alive.
    const aimTarget = this.maverickSeeker?.aiming ? 1 : 0;
    const aimAlpha = 1 - Math.exp(-MAVERICK.aimBlendRate * dt);
    this._aimBlend += (aimTarget - this._aimBlend) * aimAlpha;
    if (this._aimBlend > 1e-3 && this.fpvMaverickNode && this.tvNode) {
      this.fpvMaverickNode.getWorldPosition(_aimEye);
      this.tvNode.getWorldPosition(_tvCenter);
      _aimMatrix.lookAt(_aimEye, _tvCenter, aircraft.upVector);
      _aimQuat.setFromRotationMatrix(_aimMatrix);
      this.position.lerp(_aimEye, this._aimBlend);
      this.quaternion.slerp(_aimQuat, this._aimBlend);
    }

    // ── Layer shake + head dynamics on top (survives the Maverick aim blend) ──
    // Positional shake + head inertia/lean (world space)
    this.position.x += shakeX + _localOffset.x;
    this.position.y += shakeY + _localOffset.y;
    this.position.z += shakeZ + _localOffset.z;
    // Rotational shake + head inertia (body-local), applied as a small extra rotation
    _dynEuler.set(
      shakePitch + this._headRotOffset.x * headEffect,
      shakeYaw + this._headRotOffset.y * headEffect,
      shakeRoll + (this._headRotOffset.z + this._sofRoll) * headEffect,
    );
    _dynQuat.setFromEuler(_dynEuler);
    this.quaternion.multiply(_dynQuat);

    this._initialized = true;
  }
}
